import { useMemo } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider/Divider";
import { getCommonFacilityIcon } from "@/features/hotels/components/hotel/facilityIcons";
import { HotelImageCarousel } from "@/features/hotels/components/hotel/HotelImageCarousel";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";
import type { HotelData } from "@/features/hotels/types/types";
import {
  buildHotelFacilitiesDisplayModel,
  getLocalizedFacilityLabel,
  type HotelFacilitiesDisplayModel,
} from "@/features/hotels/utils/facilities/buildHotelFacilitiesDisplayModel";

type HotelDetailsCardProps = {
  hotel: HotelData;
  onImageTap: () => void;
  onSeeAll?: () => void;
  facilitiesModel?: HotelFacilitiesDisplayModel;
};

export function HotelDetailsCard({
  hotel,
  onImageTap,
  onSeeAll,
  facilitiesModel,
}: HotelDetailsCardProps) {
  const intl = useIntl();

  const { commonFacilities } = useMemo(
    () =>
      facilitiesModel ??
      buildHotelFacilitiesDisplayModel(hotel.descriptions?.facilities),
    [hotel.descriptions?.facilities, facilitiesModel],
  );

  const images = useMemo(
    () =>
      hotel.imagesUrls.map((url, idx) => ({
        url,
        alt: `${hotel.name} ${idx + 1}`,
      })),
    [hotel.imagesUrls, hotel.name],
  );
  const starsExist = hotel.stars != null && hotel.stars > 0;

  return (
    <section className="section-card p-0">
      {images.length ? (
        <div className="px-1 pt-1">
          <HotelImageCarousel images={images} onClick={onImageTap} />
        </div>
      ) : null}

      <div className="flex flex-col gap-4 px-4 pt-5 pb-4">
        <div>
          <div className={`${starsExist ? "mb-4" : ""} text-lg font-semibold`}>
            {hotel.name}
          </div>
          <div className="flex items-center gap-2">
            {starsExist && <HotelStars showLabel={false} stars={hotel.stars} />}
            {hotel.recommended && (
              <span className="rounded bg-surface-accent-softer px-1.5 py-1 text-sm font-semibold text-core-accent-700">
                {intl.formatMessage({
                  defaultMessage: "Rahal's Choice",
                  description: "Rahal's Choice badge label",
                })}
              </span>
            )}
          </div>
        </div>

        <Divider />

        {commonFacilities.length > 0 && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Common Facilities",
                  description:
                    "Section heading for hotel facilities and amenities",
                })}
              </h3>
              {onSeeAll ? (
                <Button variant="textFit" color="info" onClick={onSeeAll}>
                  <span className="text-xs font-medium">
                    {intl.formatMessage({
                      defaultMessage: "See all",
                      description:
                        "Action label to scroll to the full hotel facilities section",
                    })}
                  </span>
                </Button>
              ) : (
                <span />
              )}
            </div>

            <ul className="flex items-start justify-between gap-6">
              {commonFacilities.map((facility) => {
                const FacilityIcon = getCommonFacilityIcon(facility.key);

                if (!FacilityIcon) {
                  return null;
                }

                return (
                  <li
                    key={facility.key}
                    className="flex flex-col items-center gap-1"
                  >
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-accent-softer text-text-primary">
                      <FacilityIcon className="h-6 w-6" />
                    </span>
                    <span className="line-clamp-2 text-center text-xs font-medium text-text-secondary">
                      {getLocalizedFacilityLabel(
                        intl,
                        facility.localizationKey,
                        facility.label,
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
